import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSiteContent } from "@/hooks/useSiteContent";
import Wordmark from "@/components/Wordmark";
import NotificationsBell from "@/components/NotificationsBell";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

type LeafLink = { to: string; label: string; external?: boolean };
type NavItem =
  | (LeafLink & { type?: "link" })
  | { type: "dropdown"; label: string; items: LeafLink[] };

const PODCAST_URL =
  "https://open.spotify.com/show/5CQuCxSH6XDW58k51xYCDD?si=c27414fdabbf4464&nd=1&dlsi=7829d14c4e204579";

const navItems: NavItem[] = [
  { to: "/athletes", label: "Athletes" },
  { to: "/teams", label: "Teams" },
  { to: "/causes", label: "Causes" },
  { to: "/shop", label: "Brands" },
  {
    type: "dropdown",
    label: "Fan Fun",
    items: [
      { to: "/media-hub", label: "Media Hub" },
      { to: "/spotlight", label: "Spotlight" },
      { to: "/news", label: "News" },
      { to: "/scores", label: "Scores" },
      { to: PODCAST_URL, label: "Podcast", external: true },
    ],
  },
  { to: "https://sayitinsports.com", label: "Work with us", external: true },
  { to: "/contact", label: "Contact" },
];

const LeafAnchor = ({
  link,
  onClick,
  className,
}: {
  link: LeafLink;
  onClick?: () => void;
  className?: string;
}) =>
  link.external ? (
    <a
      href={link.to}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className={className}
    >
      {link.label}
    </a>
  ) : (
    <Link to={link.to} onClick={onClick} className={className}>
      {link.label}
    </Link>
  );

const Navbar = () => {
  useSiteContent();
  const { user, isAdmin, signOut } = useAuth();
  useLocation();
  const [open, setOpen] = useState(false);
  const [mobileFanFunOpen, setMobileFanFunOpen] = useState(false);

  const closeMenu = () => {
    setOpen(false);
    setMobileFanFunOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b border-border/50">
      {/* Top utility bar */}
      <div className="hidden sm:block bg-muted/40 border-b border-border/30">
        <div className="container flex items-center justify-end h-8 gap-2 text-xs font-medium">
          {user ? (
            <>
              <NotificationsBell />
              <Link
                to="/dashboard"
                className="px-3 py-1 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors"
              >
                Dashboard
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="px-3 py-1 rounded-full bg-muted font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors"
                >
                  Admin
                </Link>
              )}
              <button
                onClick={signOut}
                className="px-3 py-1 rounded-full bg-muted font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors"
              >
                Sign out
              </button>
            </>
          ) : (
            <Link
              to="/auth"
              className="px-3 py-1 rounded-full bg-muted font-semibold hover:bg-secondary hover:text-secondary-foreground transition-colors"
            >
              Sign in
            </Link>
          )}
        </div>
      </div>

      <nav className="container flex items-center justify-between h-16 gap-3">
        <Link to="/" className="shrink-0" aria-label="Say It In Sports — home">
          <Wordmark size="md" />
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-4 xl:gap-6 whitespace-nowrap text-sm font-medium">
          {navItems.map((item) => {
            if ("type" in item && item.type === "dropdown") {
              return (
                <DropdownMenu key={item.label}>
                  <DropdownMenuTrigger className="flex items-center gap-1 hover:text-primary transition-colors outline-none">
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[10rem]">
                    {item.items.map((sub) => (
                      <DropdownMenuItem key={sub.to} asChild>
                        <LeafAnchor link={sub} className="w-full cursor-pointer" />
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }
            const leaf = item as LeafLink;
            return (
              <LeafAnchor
                key={leaf.label}
                link={leaf}
                className="hover:text-primary transition-colors"
              />
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          {/* Mobile hamburger */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu panel */}
      {open && (
        <div className="lg:hidden border-t border-border/50 bg-background/95 backdrop-blur-md">
          <div className="container py-4 flex flex-col gap-1 text-base font-medium">
            {navItems.map((item) => {
              if ("type" in item && item.type === "dropdown") {
                return (
                  <div key={item.label} className="py-1">
                    <button
                      onClick={() => setMobileFanFunOpen((v) => !v)}
                      className="flex items-center gap-1 py-2 w-full text-left"
                      aria-expanded={mobileFanFunOpen}
                    >
                      {item.label}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${mobileFanFunOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {mobileFanFunOpen && (
                      <div className="pl-4 flex flex-col gap-1 border-l border-border/50 ml-2">
                        {item.items.map((sub) => (
                          <div key={sub.to} className="py-2">
                            <LeafAnchor
                              link={sub}
                              onClick={closeMenu}
                              className="hover:text-primary transition-colors"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              const leaf = item as LeafLink;
              return (
                <div key={leaf.label} className="py-2">
                  <LeafAnchor
                    link={leaf}
                    onClick={closeMenu}
                    className="hover:text-primary transition-colors"
                  />
                </div>
              );
            })}
            <div className="border-t border-border/50 mt-2 pt-3 flex flex-wrap gap-2 items-center">
              {user ? (
                <>
                  <NotificationsBell />
                  <Link
                    to="/dashboard"
                    onClick={closeMenu}
                    className="px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold"
                  >
                    Dashboard
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={closeMenu}
                      className="px-3 py-1.5 rounded-full bg-muted text-xs font-semibold"
                    >
                      Admin
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      closeMenu();
                      signOut();
                    }}
                    className="px-3 py-1.5 rounded-full bg-muted text-xs font-semibold"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <Link
                  to="/auth"
                  onClick={closeMenu}
                  className="px-3 py-1.5 rounded-full bg-muted text-xs font-semibold"
                >
                  Sign in
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
